import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { CalendarRange, ChevronDown, FileSpreadsheet, FileText, Loader2, X } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/sales/MultiSelect";
import { Chip, useCompanies } from "@/components/filters/FilterBar";
import { DateInput } from "@/components/DateInput";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDate, todayISO } from "@/lib/format";
import { Money } from "@/components/Money";
import { BrandMark, CompanyLogo } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { useSource } from "@/context/SourceContext";
import { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const SERIES = [["revenue", "Revenue", "#2563EB"], ["cogs", "Cost of Goods Sold", "#F97316"], ["total_expenses", "Total Expenses", "#16A34A"], ["net", "Net Profit/Loss", "#DC2626"]];
const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

const Spark = ({ values }) => {
  const pts = values.map((v, i) => [i, v]).filter(([, v]) => v != null);
  if (!pts.length) return null;
  const vs = pts.map(([, v]) => v);
  const lo = Math.min(...vs), hi = Math.max(...vs);
  const y = (v) => 15 - (hi > lo ? (v - lo) / (hi - lo) : 0.5) * 13;
  const step = 66 / Math.max(1, values.length - 1);
  const [li, lv] = pts[pts.length - 1];
  return (
    <svg width="66" height="16" viewBox="0 0 66 16" className="overflow-visible" aria-hidden>
      <polyline fill="none" stroke="#1F3864" strokeWidth="1.2" points={pts.map(([i, v]) => `${i * step},${y(v)}`).join(" ")} />
      <circle cx={li * step} cy={y(lv)} r="1.8" fill={lv < 0 ? "#DC2626" : "#1F3864"} />
    </svg>
  );
};

const STYLE = { bold: "font-semibold", total: "font-semibold border-t-2 border-[#1F3864]", result: "font-bold bg-slate-100 border-t-2 border-[#1F3864]", net: "font-bold bg-[#DDEBF7] border-t-2 border-[#1F3864]" };

const Grid = ({ title, rows, cols, totalLabel, testid }) => (
  <div className="overflow-x-auto rounded-md border border-slate-200" data-testid={testid}>
    <table className="w-full text-xs" style={{ minWidth: 300 + cols.length * 92 }}>
      {title && (
        <thead><tr>
          <th className="w-52 bg-[#1F3864] px-3 py-2 text-left font-bold uppercase tracking-wide text-white">{title}</th>
          <th className="w-20 bg-[#1F3864] px-2 py-2 text-left font-bold uppercase tracking-wide text-white">Trend</th>
          {cols.map((c) => (
            <th key={c.key} className="bg-[#D9E2F3] px-2 py-2 text-right font-bold uppercase text-[#1F3864]">
              {c.label}{c.partial && <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[9px] font-semibold normal-case text-amber-800" title="Partial month: only records within the dates; monthly expenses pro-rated by day" data-testid={`${testid}-partial-${c.key}`}>partial</span>}
            </th>
          ))}
          <th className="bg-[#D9E2F3] px-2 py-2 text-right font-bold uppercase text-[#1F3864]" data-testid={`${testid}-total-label`}>{totalLabel}</th>
        </tr></thead>
      )}
      <tbody>
        {rows.map((r, n) => {
          const loss = ["net", "result"].includes(r.style) && r.ytd < 0;
          return (
            <tr key={`${r.key}-${r.label}`} className={cn("border-t border-slate-100", STYLE[r.style], r.style === "line" && n % 2 === 0 && "bg-[#F3F7FC]")} data-testid={`${testid}-row-${r.key}-${r.label}`}>
              <td className={cn("w-52 whitespace-nowrap px-3 py-1.5", r.style === "line" && "pl-6", loss && "text-red-600")}>{r.label}</td>
              <td className="w-20 px-2 py-1.5"><Spark values={r.values} /></td>
              {[...r.values, r.ytd].map((v, i) => (
                <td key={i} className="whitespace-nowrap px-2 py-1.5 text-right">{v == null ? <span className="text-slate-400">—</span> : <Money value={v} />}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const BandLogo = ({ c, className }) => {
  const src = useSource();
  return src.logoUrl ? <BrandMark name={c.name} logoUrl={src.logoUrl(c)} className={className} testid={`mpl-logo-${c.id}`} /> : <CompanyLogo company={c} className={className} testid={`mpl-logo-${c.id}`} />;
};

const Band = ({ d }) => (
  <div className="rounded-md bg-[#DDEBF7] px-6 py-5 text-center" data-testid="mpl-header">
    {d.logos?.length === 1 && <BandLogo c={d.logos[0]} className={d.logos[0].has_logo ? "mx-auto mb-2 h-12 w-32" : "mx-auto mb-2 h-10 w-10 text-sm"} />}
    {d.logos?.length > 1 && (
      <div className="mb-2 flex flex-wrap items-center justify-center gap-2" data-testid="mpl-logos">
        {d.logos.map((c) => <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm"><BandLogo c={c} className={c.has_logo ? "h-5 w-10" : "h-5 w-5 rounded-full text-[9px]"} />{c.name}</span>)}
      </div>
    )}
    <div className="text-2xl font-bold text-[#1F3864] md:text-3xl" data-testid="mpl-company">{d.company}</div>
    <div className="mt-1 text-sm font-semibold text-slate-700 md:text-base">{d.title}</div>
    <div className="text-xs text-slate-600" data-testid="mpl-period">{d.period_label}</div>
    {d.companies.length > 0 && <div className="mt-1 text-xs text-slate-600" data-testid="mpl-companies">{d.companies.join(" · ")}</div>}
  </div>
);

const Chart = ({ d }) => {
  const [on, setOn] = useState(() => Object.fromEntries(SERIES.map(([k]) => [k, true])));
  const rows = d.columns.map((c, i) => ({ m: c.label.toUpperCase(), ...Object.fromEntries(d.summary.map((r) => [r.key, r.values[i]])) }));
  return (
    <div className="flex flex-col gap-4 md:flex-row" data-testid="mpl-chart">
      <div className="w-48 shrink-0 space-y-2.5 pt-2">
        <div className="text-xs font-semibold text-slate-700">Show Categories</div>
        {SERIES.map(([k, label, color]) => (
          <label key={k} className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
            <Checkbox checked={on[k]} onCheckedChange={(v) => setOn((s) => ({ ...s, [k]: !!v }))} style={{ borderColor: color }} data-testid={`mpl-toggle-${k}`} /> {label}
          </label>
        ))}
      </div>
      <div className="h-72 flex-1">
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} width={48} />
            <Tooltip formatter={(v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 }))} />
            <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} />
            {SERIES.filter(([k]) => on[k]).map(([k, label, color]) => (
              <Line key={k} dataKey={k} name={label} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} connectNulls={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const list = (v) => (v ? v.split(",").filter(Boolean) : []);
const monthsBetween = (a, b) => (+b.slice(0, 4) - +a.slice(0, 4)) * 12 + (+b.slice(5, 7) - +a.slice(5, 7)) + 1;

const MonthPicker = ({ value, onChange, disabled }) => {
  const cleared = value.includes(-1);
  const cur = cleared ? [] : value.length ? value : MONTHS.map((_, i) => i + 1);
  const label = cleared ? "No months" : cur.length === 12 ? "All months" : cur.map((m) => MONTHS[m - 1]).join(", ");
  const toggle = (m) => {
    const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b);
    onChange(next.length === 12 ? [] : next.length ? next : [-1]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="h-9 max-w-[220px] gap-1.5 bg-white" data-testid="mpl-months-trigger">
          <span className="truncate">{label}</span><ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="end">
        <div className="mb-2 flex justify-between text-xs">
          <button type="button" className="font-medium text-teal-700 hover:underline" onClick={() => onChange([])} data-testid="mpl-months-all">All months</button>
          <button type="button" className="font-medium text-slate-500 hover:underline" onClick={() => onChange([-1])} data-testid="mpl-months-clear">Clear</button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS.map((n, i) => (
            <label key={n} className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-1 text-xs hover:bg-slate-50">
              <Checkbox checked={cur.includes(i + 1)} onCheckedChange={() => toggle(i + 1)} data-testid={`mpl-month-${i + 1}`} /> {n}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Exports = ({ qs, name }) => {
  const { download, canExport = true } = useSource();
  const [busy, setBusy] = useState("");
  if (!canExport) return null;
  const run = async (fmt) => {
    setBusy(fmt);
    try { await download(`/reports/monthly-pnl/export?${qs}&format=${fmt}`, `monthly_pnl_${name}.${fmt}`); } catch (_) { toast.error("Export failed. Please try again."); } finally { setBusy(""); }
  };
  return (
    <div className="flex gap-2">
      {[["xlsx", "Excel", FileSpreadsheet], ["pdf", "PDF", FileText]].map(([fmt, label, Icon]) => (
        <Button key={fmt} variant="outline" size="sm" className="gap-1.5 bg-white" disabled={!!busy} onClick={() => run(fmt)} data-testid={`mpl-export-${fmt}`}>
          {busy === fmt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} Export {label}
        </Button>
      ))}
    </div>
  );
};

const YearTiles = ({ tiles, selected, onPick }) => (
  <div className="-mx-6 flex gap-3 overflow-x-auto px-6 pb-1 md:mx-0 md:flex-wrap md:overflow-visible md:px-0" data-testid="mpl-year-tiles">
    {tiles.map((t) => (
      <button key={t.year} type="button" onClick={() => onPick(t.year)} data-testid={`mpl-year-tile-${t.year}`}
        className={cn("min-w-[200px] shrink-0 rounded-xl border px-4 py-3 text-left transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md md:min-w-[210px] md:flex-1 md:max-w-[280px]",
          t.year === selected ? "border-[#1F3864] bg-[#DDEBF7] ring-1 ring-[#1F3864]" : "border-slate-200 bg-white")}>
        <div className="text-xs font-semibold text-[#1F3864]">{t.label}</div>
        {[["Gross profit", t.gross], ["Net profit/loss", t.net]].map(([l, v]) => (
          <div key={l} className="mt-1.5 flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-slate-500">{l}</span>
            <span className={cn("text-sm font-bold tabular-nums", v >= 0 ? "text-green-700" : "text-red-700")} data-testid={`mpl-year-tile-${t.year}-${l.startsWith("Gross") ? "gross" : "net"}`}><Money value={v} /></span>
          </div>
        ))}
      </button>
    ))}
  </div>
);

export const MonthlyPnl = () => {
  const src = useSource();
  const { user } = useAuth() || {};
  const superAdmin = !src.readOnly && user?.is_super_admin;
  const companies = useCompanies(superAdmin);
  const [sp, setSp] = useSearchParams();
  const [bmin, bmax] = src.bounds || [];
  const today = todayISO();
  const clamp = (x) => (bmin && x < bmin ? bmin : bmax && x > bmax ? bmax : x);
  const defTo = clamp(today);
  const defFrom = clamp("2024-01-01") > defTo ? bmin : clamp("2024-01-01");
  const picked = list(sp.get("mpl_co"));
  const rawMonths = list(sp.get("mpl_m")).map(Number);
  const months = rawMonths.filter((m) => m >= 1 && m <= 12);
  const from = sp.get("mpl_from") || defFrom;
  const to = sp.get("mpl_to") || defTo;
  const isDefault = from === defFrom && to === defTo;
  const year = Number(from.slice(0, 4));
  const oneYear = from.slice(0, 4) === to.slice(0, 4) ? year : null;
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const update = (patch) => setSp((prev) => {
    const p = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v === "" || v == null || (Array.isArray(v) && !v.length) ? p.delete(k) : p.set(k, Array.isArray(v) ? v.join(",") : String(v))));
    return p;
  }, { replace: true });
  const pickYear = (y) => update({ mpl_from: clamp(`${y}-01-01`), mpl_to: clamp(String(y) === today.slice(0, 4) ? today : `${y}-12-31`) });
  const error = from > to ? "From date must be before To date" : monthsBetween(from, to) > 120 ? "Date range supports up to 120 months" : rawMonths.includes(-1) ? "Pick at least one month" : null;
  const qs = useMemo(() => {
    const q = new URLSearchParams();
    q.set("from", from); q.set("to", to);
    months.forEach((m) => q.append("month", m));
    picked.forEach((c) => q.append("company", c));
    return q.toString();
  }, [from, to, months.join(","), picked.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) return undefined;
    const ctl = new AbortController();
    setLoading(true);
    src.get(`/reports/monthly-pnl?${qs}`, { signal: ctl.signal })
      .then((r) => { setD(r.data); setLoading(false); })
      .catch((e) => { if (!axios.isCancel(e)) { toast.error(apiError(e)); setLoading(false); } });
    return () => ctl.abort();
  }, [src, qs, error]);

  const coName = (id) => companies.find((c) => c.value === id)?.label || id;
  const chips = [
    ...picked.map((c) => ({ key: `co-${c}`, label: `Company: ${coName(c)}`, remove: () => update({ mpl_co: picked.filter((x) => x !== c) }) })),
    { key: "range", label: `Date: ${fmtDate(from)} – ${fmtDate(to)}`, remove: isDefault ? undefined : () => update({ mpl_from: "", mpl_to: "" }) },
    ...(months.length && months.length < 12 ? [{ key: "months", label: `Months: ${months.map((m) => MONTHS[m - 1]).join(", ")}`, remove: () => update({ mpl_m: "" }) }] : []),
  ];
  const years = Array.from(new Set([...(d?.years || []), ...(oneYear ? [oneYear] : [])])).sort((a, b) => b - a);
  return (
    <section className="rise-in rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="report-monthly_pnl">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Monthly Profit and Loss Statement</h2>
          <p className="mt-0.5 text-xs text-slate-500">January–December side by side with year to date · gross profit = GP Value − sales discounts</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" data-testid="mpl-loading" />}
          {superAdmin && <MultiSelect label="Company" staticOptions={companies} value={picked} onChange={(v) => update({ mpl_co: v })} testid="mpl-company-filter" chips={false} />}
          <Select value={oneYear ? String(oneYear) : ""} onValueChange={(v) => pickYear(Number(v))}>
            <SelectTrigger className="h-9 w-28 bg-white" data-testid="mpl-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`mpl-year-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <MonthPicker value={rawMonths} onChange={(v) => update({ mpl_m: v })} />
          {!error && <Exports qs={qs} name={`${from}_${to}`} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-6 py-3" data-testid="mpl-range">
        <CalendarRange className="h-4 w-4 text-teal-600" />
        <span className="text-xs font-medium text-slate-600">Date range</span>
        <DateInput value={from} min={bmin} max={bmax} onChange={(x) => update({ mpl_from: x })} className="h-8 w-36" testid="mpl-from-input" />
        <span className="text-xs text-slate-400">to</span>
        <DateInput value={to} min={bmin} max={bmax} onChange={(x) => update({ mpl_to: x })} className="h-8 w-36" testid="mpl-to-input" />
        {!isDefault && <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => update({ mpl_from: "", mpl_to: "" })} data-testid="mpl-clear-range"><X className="h-3.5 w-3.5" /> Clear range</Button>}
        {error && <span className="text-xs font-medium text-red-600" data-testid="mpl-error">{error}</span>}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 px-6 pt-4" data-testid="mpl-chips">
          {chips.map((c) => <Chip key={c.key} label={c.label} onRemove={c.remove} testid={`mpl-chip-${c.key}`} />)}
        </div>
      )}
      {d && (
        <div className={cn("space-y-5 p-6 transition-opacity", loading && "opacity-50")}>
          <Band d={d} />
          {d.note && <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="mpl-excluded-note">{d.note}</p>}
          {d.year_tiles?.length > 0 && <YearTiles tiles={d.year_tiles} selected={oneYear} onPick={pickYear} />}
          {d.columns.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500" data-testid="mpl-empty">No months match the selected range and months</p>
          ) : (<>
          <Chart d={d} />
          <p className="text-[11px] font-medium text-[#1F3864]" data-testid="mpl-filters-line">{d.filters_line}</p>
          <Grid title="Category" rows={d.summary} cols={d.columns} totalLabel={d.total_label} testid="mpl-summary" />
          {d.year_blocks?.length > 0 && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:p-4" data-testid="mpl-year-blocks">
              <h3 className="text-sm font-bold text-[#1F3864]">Year-by-year summary</h3>
              {d.year_blocks.map((b) => (
                <div key={b.year} className="space-y-2" data-testid={`mpl-year-block-${b.year}`}>
                  <span className="inline-flex rounded-full bg-[#1F3864] px-3 py-1 text-xs font-semibold text-white">{b.label}</span>
                  <Grid title="Category" rows={b.summary} cols={b.columns} totalLabel={b.total_label} testid={`mpl-year-block-grid-${b.year}`} />
                </div>
              ))}
            </div>
          )}
          {d.sections.map((s) => <Grid key={s.key} title={s.title} rows={s.rows} cols={d.columns} totalLabel={d.total_label} testid={`mpl-${s.key}`} />)}
          <p className="text-[11px] text-slate-500">Months with no data show —; the {d.total_label} column sums the available months.{d.columns.some((c) => c.partial) && " Partial months include only records within the dates; monthly expense records are pro-rated by day."}</p>
          </>)}
        </div>
      )}
    </section>
  );
};
